import React, { useEffect, useRef, useState } from 'react'

import VolumeOverallTimer from './Components/VolumeOverallTimer'
import VolumeEvaluationTimer from './Components/VolumeEvaluationTimer'
import VolumeInitialReminders from './Components/VolumeInitialReminders'
import VolumeCheckTeacherModal from './Components/VolumeCheckTeacherModal'
import handleTeacherModeSpeech from './functions/handleTeacherModeSpeech'
import handleStudentModeSpeech from './functions/handleStudentModeSpeech'
import useDetectStudentDidSaySomething from './hooks/useDetectStudentDidSaySomething'
import { PROGRESS_STATES, SPEECH_VOLUME_CHECK_STATES, PROGRESS_STATES_ORDER } from './StudentVolumeChecksConstants'

import TestButton from '../Components/TestButton'
import useStudentPreChecks from '../useStudentPreChecks'
import VolumeMeter from '../Components/VolumeMeter/VolumeMeter'
import { PRE_CHECK_MACHINE_STATES } from '../constants'
import useControlledSpeech from '../Components/hooks/useControlledSpeech'
import StudentInstruction from '../Components/StudentInstruction'
import VolumeCheckSpot from './Components/VolumeCheckSpot'
import { useVolumeCheckSpot } from './Components/hooks/useVolumeCheckSpot'

export default function VolumeCheckModes(props) {

    /**
     * props
     */
    const { speechProps, toggleReaderContainer, showMicIcon, teacherCalled, teacherTestSuccessful, isTeacherMode, toggleTeacherMode } = props

    /**
    * states
    */
    const [volumeStats, getVolumeStats] = useState({})
    const [currentProgressState, setCurrentProgressState] = useState(PROGRESS_STATES.INITIAL)
    const [showTeacherTroubleshootScreen, setShowTeacherTroubleshootScreen] = useState(false)
    const [currentSpeechVolumeCheckState, setCurrentSpeechVolumeCheckState] = useState(SPEECH_VOLUME_CHECK_STATES.INITIAL)
    const [acceptedAtKindOfLoudVolume, setAcceptedAtKindOfLoudVolume] = useState(false)

    /**
     * hooks
     */
    const { speakWith } = useControlledSpeech({ speechProps })
    const { studentDidSaySomething, resetDidSaySomething } = useDetectStudentDidSaySomething({ volumeStats })
    const studentPreCheck = useStudentPreChecks({ itemToCheck: PRE_CHECK_MACHINE_STATES.studentSpeechVolumeChecks });
    const spotHook = useVolumeCheckSpot(speechProps)

    const { isCurrent, settings } = studentPreCheck;

    /**
     * mutable values, remember to use refVariable.current to access or mutate the value!
     */
    const studentSpeechVolumeEvaluationCount = useRef(0)

    /**
     * Wait to start the volume check
     */
    useEffect(() => {
        if (isCurrent) {
            speechProps.abortSpeech(stepThroughProgressStates)
        }

        return () => {
            speechProps.abortSpeech()
        }
    }, [currentProgressState])

    /**
     * Waiting to start the volume check evaluation step
     */
    useEffect(() => {
        if (studentDidSaySomething.current && currentSpeechVolumeCheckState === SPEECH_VOLUME_CHECK_STATES.WAITING_FOR_INITIAL_SPEECH) {
            console.log("Volume activity started, student said something detected")
            setCurrentSpeechVolumeCheckState(SPEECH_VOLUME_CHECK_STATES.ACTIVITY_IN_PROGRESS)
        }
    }, [studentDidSaySomething.current, currentSpeechVolumeCheckState])

    /**
     * Methods
     */
    const stepThroughProgressStates = async () => {
        try {
            if (isTeacherMode) {
                teacherCalled.current = true
                await handleTeacherModeSpeech({ speechProps, currentProgressState, settings, goToNextState, setCurrentSpeechVolumeCheckState, acceptedAtKindOfLoudVolume, toggleReaderContainer, showMicIcon, setShowTeacherTroubleshootScreen, spotHook })
            } else[
                await handleStudentModeSpeech({ speakWith, speechProps, currentProgressState, settings, goToNextState, setCurrentSpeechVolumeCheckState, acceptedAtKindOfLoudVolume, toggleReaderContainer, showMicIcon, teacherCalled, setCurrentProgressState, spotHook })
            ]

            if (currentProgressState === PROGRESS_STATES.SPOT_EXIT_SCREEN) {
                finishCheck()
            }
        } catch (err) {
            studentPreCheck.failedCheck(new Error("Volume check failed", { cause: err }))
        }
    }

    /**
     * This method should only be called once when it is finished everything.
     */
    const finishCheck = async () => {
        setCurrentSpeechVolumeCheckState(SPEECH_VOLUME_CHECK_STATES.ACTIVITY_COMPLETED)
        studentPreCheck.finishCheck()
    }
    const onAcceptableVolumeCheckPassed = (options) => {
        if (isTeacherMode) {
            teacherTestSuccessful.current = true
        }
        if(options && options.loudButAcceptable){
            setAcceptedAtKindOfLoudVolume()
        }
        goToNextState()
    }
    const retry = () => {
        resetStatesToBeginningOfCheck()
        studentPreCheck.retryWith(stepThroughProgressStates)
    }
    const resetStatesToBeginningOfCheck = () => {
        studentSpeechVolumeEvaluationCount.current = 0
        setCurrentSpeechVolumeCheckState(SPEECH_VOLUME_CHECK_STATES.WAITING_FOR_INITIAL_SPEECH)
        resetDidSaySomething()
        setShowTeacherTroubleshootScreen(false)
    }
    const goToNextState = () => {
        const currentProgressStateIndex = PROGRESS_STATES_ORDER.indexOf(currentProgressState)
        if (currentProgressStateIndex < PROGRESS_STATES_ORDER.length - 1) {
            setCurrentProgressState(PROGRESS_STATES_ORDER[currentProgressStateIndex + 1])
        }
    }

    /**
     * start rendering
     */
    if (!studentPreCheck.isCurrent) return null

    /**
     * handle unexpected errors
     */
    if (studentPreCheck.currentError) {
        return <StudentInstruction>
            <p>Something went wrong with your speech volume</p>
            <TestButton action="Retry" currentCheck={PRE_CHECK_MACHINE_STATES.studentSpeechVolumeChecks} retry={retry} />
        </StudentInstruction>
    }

    /**
     * calling teacher for help / troubleshoot screens
     */
    if (showTeacherTroubleshootScreen) {
        return <VolumeCheckTeacherModal
            volumeStats={volumeStats}
            studentDidSaySomething={studentDidSaySomething}
            handleRetry={retry}
            isTeacherMode={isTeacherMode}
            toggleTeacherMode={toggleTeacherMode}
            teacherTestSuccessful={teacherTestSuccessful} />
    }

    // iPad required the volume meter to be rendered at all times to work
    const waitingForEvaluation = currentSpeechVolumeCheckState === SPEECH_VOLUME_CHECK_STATES.ACTIVITY_IN_PROGRESS
    const waitingForInitialSpeech = currentProgressState === PROGRESS_STATES.SPOT_ON_SCREEN_2 && currentSpeechVolumeCheckState === SPEECH_VOLUME_CHECK_STATES.WAITING_FOR_INITIAL_SPEECH && !studentDidSaySomething.current
    const shouldRenderVolumeMeter = !speechProps.isSpeaking && (currentSpeechVolumeCheckState === SPEECH_VOLUME_CHECK_STATES.WAITING_FOR_INITIAL_SPEECH || currentSpeechVolumeCheckState === SPEECH_VOLUME_CHECK_STATES.ACTIVITY_IN_PROGRESS)
    /**
     * the main volume check components
     */
    return <React.Fragment>
        <VolumeOverallTimer {...{ speechProps, setShowTeacherTroubleshootScreen, volumeStats, studentSpeechVolumeEvaluationCount }} />
        {waitingForInitialSpeech && <VolumeInitialReminders {...{ speechProps, isTeacherMode, setShowTeacherTroubleshootScreen }} />}
        {waitingForEvaluation && <VolumeEvaluationTimer {...{ speechProps, volumeStats, studentSpeechVolumeEvaluationCount, isTeacherMode, setShowTeacherTroubleshootScreen, onAcceptableVolumeCheckPassed }} />}
        <StudentInstruction>
            {isTeacherMode && <p>When the microphone turns green, say "Wake up, Spot"</p>}
            <VolumeCheckSpot state={spotHook.spotState}/>
        </StudentInstruction>
        {!showTeacherTroubleshootScreen && shouldRenderVolumeMeter &&
            <VolumeMeter
                key={currentProgressState} // This is important for the recorder stats to work on iOS, it required at least 1 rerender of this component, really need to get the to bottom of it though.
                speechProps={speechProps}
                getVolumeStats={getVolumeStats}
                shouldStartVolumeProcessing={isCurrent} />}
    </React.Fragment>
}